import React, { useEffect, useState } from "react";
import { notifyError } from "../../utils/tostfy";
import { Link, useOutletContext, useSearchParams } from "react-router-dom";
import { handleApiCall } from "../../apis";
import { endpoint } from "../../utils/endpoints";
import EyeIcon from '../../assets/image/eye-icon.svg'
import LeftAngle from '../../assets/image/left-angle.svg'
import RightAngle from '../../assets/image/right-angle.svg'
import { formatTripDateTime } from "../../utils/formatDateTime";
import PageNumberInput from "../../Components/Pagination/PageNumberInput";
const pickAccountant = (d) => {
  if (!d || typeof d !== "object") return null;
  if (d.accountant) return d.accountant;
  if (d.accountant_data) return d.accountant_data;
  if (d.full_name != null || d.employee_id != null || d.email != null) return d;
  return d;
};

const normalizeAccountant = (raw) => {
  const a = pickAccountant(raw) || raw || {};
  const code = (a.country_code || "").trim();
  const num = (a.mobile_number ?? a.mobile ?? "").toString().trim();
  const phone =
    code && num ? `${code} ${num}` : num || code || "—";
  return {
    fullName: a.full_name ?? a.name ?? "—",
    employeeId: a.employee_id ?? a.employeeId ?? "—",
    email: a.email ?? "—",
    miplLocation:
      a.mipl_location_id?.name ||
      a.mipl_location?.name ||
      a.location_name ||
      "—",
    phone,
    status: a.status === 1 ? "Active" : a.status === 0 ? "Inactive" : "—",
  };
};

const mapTripRow = (t, index) => ({
  id: t?._id || t?.id || `trip-${index}`,
  tripId:
    t?.trip_ref_id != null
      ? String(t.trip_ref_id)
      : t?.trip_id ?? t?.tripId ?? t?.trip_code ?? "—",
  tripStart: formatTripDateTime(t?.trip_start_at),
  tripEnd: formatTripDateTime(t?.trip_end_at),
  company: t?.company_id?.name ?? t?.company?.name ?? t?.company_name ?? "—",
  location:
    t?.company_id?.company_location_id?.name ??
    t?.company_location_id?.name ??
    t?.company_location?.name ??
    t?.location ??
    "—",
  vehicle:
    t?.vehicle_id?.registration_number ??
    t?.vehicle_id?.name ??
    t?.vehicle?.registration_number ??
    t?.vehicle_number ??
    t?.vehicle ??
    "—",
  challanNumber:
    t?.challan_number != null && String(t.challan_number).trim() !== ""
      ? String(t.challan_number)
      : "—",
  tripBreakLocation:
    t?.trip_break_location_id?.name ??
    t?.trip_break_location?.name ??
    t?.trip_break_location_name ??
    "—",
  status:
    typeof t?.status === "string"
      ? t.status
      : t?.status === 0
        ? "Pending"
        : t?.status === 1
          ? "Started"
          : t?.status === 2
            ? "Locked"
            : t?.status === 3
              ? "Completed"
              : t?.status === 4
                ? "Cancelled"
                : t?.trip_status ?? "—",
});

const trimText = (value, max = 18) => {
  if (value == null) return "—";
  const text = String(value);
  if (text.length <= max) return text;
  return `${text.slice(0, max).trimEnd()}...`;
};

const ViewAccountantDetails = () => {
  const { setSubheading } = useOutletContext();
  const [searchParams] = useSearchParams();
  const accountantId = (searchParams.get("accountant_id") || "").trim();

  const [loading, setLoading] = useState(true);
  const [accountant, setAccountant] = useState(null);

  const [drivers, setDrivers] = useState([]);
  const [tripsLoading, setTripsLoading] = useState(() => Boolean(accountantId));
  const [tripsTotal, setTripsTotal] = useState(0);
  const [tripsTotalPages, setTripsTotalPages] = useState(1);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  useEffect(() => {
    setSubheading("View Accountant Details");
    return () => {
      setSubheading("");
    };
  }, [setSubheading]);

  useEffect(() => {
    const load = async () => {
      if (!accountantId) {
        setLoading(false);
        notifyError("Accountant ID is missing");
        setAccountant(null);
        setDrivers([]);
        return;
      }
      setLoading(true);
      try {
        const response = await handleApiCall(
          "get",
          `${endpoint.viewAccountant}?accountant_id=${encodeURIComponent(accountantId)}`,
          {}
        );
        if (response?.data?.code === 200) {
          const d = response?.data?.data;
          setAccountant(normalizeAccountant(d));
        } else {
          setAccountant(null);
          setDrivers([]);
          const errMsg =
            response?.response?.data?.errorMessage ||
            response?.response?.data?.message ||
            response?.data?.message ||
            "Failed to load accountant details";
          notifyError(errMsg);
        }
      } catch {
        setAccountant(null);
        setDrivers([]);
        notifyError("Failed to load accountant details");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [accountantId]);

  useEffect(() => {
    const loadTrips = async () => {
      if (!accountantId) {
        setDrivers([]);
        setTripsTotal(0);
        setTripsTotalPages(1);
        setTripsLoading(false);
        return;
      }
      setTripsLoading(true);
      try {
        const q = `page=${currentPage}&limit=${itemsPerPage}&accountant_id=${encodeURIComponent(accountantId)}`;
        const response = await handleApiCall(
          "get",
          `${endpoint.listOfAssignedTrip}?${q}`,
          {}
        );
        if (response?.data?.code === 200) {
          const d = response?.data?.data || {};
          const raw = d?.assigned_trip_data;
          const list = Array.isArray(raw) ? raw : [];
          setDrivers(list.map(mapTripRow));
          setTripsTotal(typeof d?.total === "number" ? d.total : list.length);
          setTripsTotalPages(Math.max(1, d?.total_pages ?? 1));
        } else {
          setDrivers([]);
          setTripsTotal(0);
          setTripsTotalPages(1);
          const errMsg =
            response?.response?.data?.errorMessage ||
            response?.response?.data?.message ||
            response?.data?.message ||
            "Failed to load assigned trips";
          notifyError(errMsg);
        }
      } catch {
        setDrivers([]);
        setTripsTotal(0);
        setTripsTotalPages(1);
        notifyError("Failed to load assigned trips");
      } finally {
        setTripsLoading(false);
      }
    };
    loadTrips();
  }, [accountantId, currentPage, itemsPerPage]);

  const indexOfFirst = (currentPage - 1) * itemsPerPage;
  const currentDrivers = drivers;
  const totalPages = tripsTotalPages;

  return (
    <>
    <div className="md:mt-7 mt-3">
        <div className="flex items-center mb-3 md:mb-0">
            <h3 className="md:text-xl font-UbuntuMedium md:!mb-2">Accountant Details</h3>
            <div className="ml-auto flex gap-2">
              <Link to="/accountant-management" className="btn btn-primary">Back</Link>
            </div>
        </div>
        <div className="mb-4 max-w-screen-lg rounded-lg bg-white px-3 py-2 lg:px-4 lg:py-2 shadow-sm">
          {loading ? (
            <p className="text-gray-500 py-4 mb-0">Loading...</p>
          ) : !accountantId ? (
            <p className="text-gray-500 py-4 mb-0">No accountant selected.</p>
          ) : !accountant ? (
            <p className="text-gray-500 py-4 mb-0">Unable to load accountant details.</p>
          ) : (
          <ul className="grid md:grid-cols-2 [&_li]:flex [&_li]:py-1.5 [&_li]:justify-between md:[&_li:nth-child(odd)]:pr-12 [&_li:nth-child(odd)]:border-r md:[&_li:nth-child(even)]:pl-12 font-UbuntuMedium [&_.label]:text-gray-400 max-[767px]:text-sm">
            <li>
              <div className="label">Accountant Name</div>
              <div className="font-UbuntuMedium">{accountant?.fullName}</div>
            </li>
            <li>
              <div className="label">Employee ID</div>
              <div className="font-UbuntuMedium">{accountant?.employeeId}</div>
            </li>
            <li>
              <div className="label">Email</div>
              <div className="font-UbuntuMedium">{accountant?.email}</div>
            </li>
            <li>
              <div className="label">MIPL Location</div>
              <div className="font-UbuntuMedium">{accountant?.miplLocation}</div>
            </li>
            <li>
              <div className="label">Phone Number</div>
              <div className="font-UbuntuMedium">{accountant?.phone}</div>
            </li>
            <li>
              {/* <div className="label">Status</div>
              <div className="font-UbuntuMedium">{accountant?.status}</div> */}
            </li>
          </ul>
          )}
        </div>

        <h3 className="md:text-xl font-UbuntuMedium !mb-2.5">All Assigned Trips</h3>
        <div className="common-table-wrapper m-0 shadow-sm">
            <div className="table-responsive">
                <table>
                    <thead>
                        <tr>
                            <th>S.No.</th>
                            <th>Trip ID</th>
                            <th>Trip Start</th>
                            <th>Trip End</th>
                            <th>Company</th>
                            <th>Company Location</th>
                            <th>Vehicle</th>
                            <th>Challan Number</th>
                            <th>Trip Break Location</th>
                            <th>Status</th>
                            <th>Action</th>
                        </tr>
                    </thead>

                    <tbody>
                        {tripsLoading && (
                          <tr>
                            <td colSpan={11} className="text-center py-4 text-gray-500">Loading...</td>
                          </tr>
                        )}
                        {!tripsLoading && currentDrivers.map((driver, index) => (
                            <tr key={driver.id}>
                                <td>{indexOfFirst + index + 1}</td>
                                <td>{driver.tripId}</td>
                                <td className="whitespace-nowrap">{driver.tripStart}</td>
                                <td className="whitespace-nowrap">{driver.tripEnd}</td>
                                <td>{driver.company}</td>
                                <td>{driver.location}</td>
                                <td>{driver.vehicle}</td>
                                <td title={driver.challanNumber}>{trimText(driver.challanNumber)}</td>
                                <td>{driver.tripBreakLocation}</td>
                                <td>{driver.status}</td>
                                <td>
                                    <div className="flex items-center justify-center gap-2">
                                        <Link
                                          to={`/accountant-management/view-trip?accountant_id=${encodeURIComponent(accountantId)}&trip_id=${encodeURIComponent(String(driver.id))}`}
                                        >
                                          <img src={EyeIcon} className="h-7" alt="View trip"/>
                                        </Link>
                                    </div>
                                </td>
                            </tr>
                        ))}
                        {!tripsLoading && currentDrivers.length === 0 && (
                          <tr>
                            <td colSpan={11} className="text-center py-4 text-gray-500">No assigned trips</td>
                          </tr>
                        )}
                    </tbody>
                </table>
            </div>
        </div>
        <div className="flex gap-x-4 items-center mt-4">

            <select
                className="p-2 shadow-sm outline-none max-[767px]:text-xs"
                value={itemsPerPage}
                onChange={(e) => {
                    setItemsPerPage(Number(e.target.value));
                    setCurrentPage(1);
                }}
            >
                <option value={10}>10</option>
                <option value={20}>20</option>
            </select>
            <div className="text-xs md:text-sm text-gray-500 ml-auto">
                {tripsTotal} records found
            </div>
            <div className="flex items-center gap-2 md:pr-8">
                <button
                    type="button"
                    onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                    disabled={currentPage === 1}
                    className={`!bg-primary text-white md:w-[35px] md:h-[35px] w-[25px] h-[25px] rounded-full disabled:opacity-50 inline-flex justify-center items-center ${currentPage===1 ? '' : 'cursor-pointer'}`}
                >
                    <img src={LeftAngle} className="invert grayscale brightness-100" alt=""/>
                </button>
                <PageNumberInput
              page={currentPage}
              totalPages={totalPages}
              onPageChange={setCurrentPage}
              className="h-[35px] w-10 md:w-12 rounded border border-gray-300 text-center text-sm font-UbuntuMedium shadow-sm outline-none disabled:cursor-not-allowed disabled:bg-gray-100 disabled:opacity-70"
            />
                <span className="text-sm text-gray-600 text-nowrap">
                    of {totalPages}
                </span>
                <button
                    type="button"
                    onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
                    disabled={currentPage === totalPages || tripsTotal === 0}
                    className="!bg-primary text-white md:w-[35px] md:h-[35px] w-[25px] h-[25px] rounded-full disabled:opacity-50 inline-flex justify-center items-center cursor-pointer"
                >
                    <img src={RightAngle} className="invert grayscale brightness-100" alt=""/>
                </button>

            </div>
        </div>
      </div>
    </>
  );
};

export default ViewAccountantDetails;
